import React, { useContext } from 'react';
import { Typography, Grid, Accordion, AccordionDetails, Switch, AccordionSummary, FormControl, FormControlLabel, Radio, RadioGroup, } from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import EventNoteIcon from '@mui/icons-material/EventNote';
import ExploreIcon from '@mui/icons-material/Explore';
import SettingsIcon from '@mui/icons-material/Settings';
import { DatasetSelectionCtx } from '../../App/ApplicationContext';
import { DateWidget } from './DateWidget';
import { CoordinateWidget } from './CoordinateWidget';
import { NoTimespanAlert, SomeTimespanAlert, ParameterUnsetAlert, SubsettingAlert, } from '../../GenericUI/Alerts';
import { NOCOORDINATES, NODATE, DEFAULTCOORDINATES, } from '../../../types/dataTypes';
import { SUBSETTING } from '../../../types/searchTypes';
const classes = {
    root: {
        width: '100%',
        minHeight: '50vh',
    },
    heading: {
        minWidth: '100%',
        display: 'flex',
        justifyContent: 'center',
    },
    description: {
        color: 'rgba(0, 0, 0, 0.6)',
        textAlign: 'center',
        paddingBottom: '30px',
        marginTop: '-20px',
    },
    title: {
        fontSize: '18px',
        marginLeft: '8px',
    },
    details: {
        display: 'flex',
        flexDirection: 'column',
    },
    checkboxLabel: {
        position: 'absolute',
        left: '10px',
        top: '10px',
    },
    textFieldWrapper: { display: 'flex', justifyContent: 'center' },
};
export const ParameterSelector = ({ temporalParameters, setTemporalParameter, spatialParameters, setSpatialParameter, subsettingMode, rookOperation, setRookOperation, }) => {
    const [spatialParametersEnabled, setSpatialParametersEnabled] = React.useState(true);
    const [temporalParametersEnabled, setTemporalParametersEnabled] = React.useState(true);
    const toggleSpatial = () => {
        if (spatialParametersEnabled) {
            setSpatialParametersEnabled(false);
            setSpatialParameter(NOCOORDINATES);
        }
        else {
            setSpatialParametersEnabled(true);
            setSpatialParameter(DEFAULTCOORDINATES);
        }
    };
    const toggleTemporal = () => {
        if (temporalParametersEnabled) {
            setTemporalParametersEnabled(false);
            setTemporalParameter(NODATE);
        }
        else {
            setTemporalParametersEnabled(true);
            setTemporalParameter(temporalParameters);
        }
    };
    const { datasetCollection } = useContext(DatasetSelectionCtx);
    const datasetsWithTimeSpan = datasetCollection
        .filter((dataset) => dataset.startTime && dataset.endTime)
        .reduce((withTimeSpan, dataset) => withTimeSpan.concat(dataset), []);
    const allDatasetsHaveTimespan = datasetsWithTimeSpan.length - datasetCollection.length === 0;
    const timeSpanAvailable = datasetsWithTimeSpan.length > 0;
    const handleChange = (event) => {
        setRookOperation(event.target.value);
    };
    return (React.createElement("div", { style: classes.root },
        React.createElement(SubsettingAlert, null),
        subsettingMode === SUBSETTING.ROOK && (React.createElement(Accordion, { key: 'facetSelection', expanded: true, onChange: () => { } },
            React.createElement(AccordionSummary, { expandIcon: null, "aria-controls": 'facetSelection', id: 'facetSelection' },
                React.createElement(Typography, { sx: classes.heading },
                    React.createElement(SettingsIcon, null),
                    React.createElement("div", { style: classes.title }, "Operation Selection"))),
            React.createElement(AccordionDetails, { sx: classes.details },
                React.createElement(Grid, { item: true, xs: 12 },
                    React.createElement(Typography, { sx: classes.description }, "Operation to be used for rook subsetting")),
                React.createElement("div", { style: classes.textFieldWrapper },
                    React.createElement(FormControl, { component: 'fieldset' },
                        React.createElement(RadioGroup, { "aria-label": 'operation', name: 'operation-selection', value: rookOperation, onChange: handleChange, row: true },
                            React.createElement(FormControlLabel, { value: 'average', control: React.createElement(Radio, { color: 'primary' }), label: 'Average' }),
                            React.createElement(FormControlLabel, { value: 'noop', control: React.createElement(Radio, { color: 'primary' }), label: 'No Op' }))))))),
        React.createElement(Accordion, { key: 'spatialParameters', expanded: true, onChange: () => { } },
            React.createElement(AccordionSummary, { expandIcon: null, "aria-controls": 'spatialParameters', id: 'spatialParameters' },
                React.createElement(Typography, { sx: classes.heading },
                    React.createElement(Switch, { checked: spatialParametersEnabled, onChange: toggleSpatial, color: 'primary', name: 'spatialParameters', inputProps: { 'aria-label': 'primary checkbox' }, sx: classes.checkboxLabel }),
                    React.createElement(ExploreIcon, null),
                    React.createElement("div", { style: classes.title }, "Spatial Parameters"))),
            React.createElement(AccordionDetails, { sx: classes.details },
                React.createElement(Grid, { item: true, xs: 12 },
                    React.createElement(Typography, { sx: classes.description }, "Coordinates of the bounding box in lat / lon format")),
                spatialParametersEnabled && (React.createElement(CoordinateWidget, { spatialParameters: spatialParameters, setSpatialParameter: setSpatialParameter })),
                !spatialParametersEnabled && React.createElement(ParameterUnsetAlert, null))),
        React.createElement(Accordion, { key: 'temporalParameters', expanded: true, onChange: () => { } },
            React.createElement(AccordionSummary, { expandIcon: null, "aria-controls": 'temporalParameters', id: 'temporalParameters' },
                React.createElement(Typography, { sx: classes.heading },
                    React.createElement(Switch, { checked: temporalParametersEnabled, onChange: toggleTemporal, color: 'primary', name: 'temporalParametersEnabled', inputProps: { 'aria-label': 'primary checkbox' }, sx: classes.checkboxLabel }),
                    React.createElement(EventNoteIcon, null),
                    React.createElement("div", { style: classes.title }, "Temporal Parameters"))),
            React.createElement(AccordionDetails, { sx: classes.details },
                React.createElement(Grid, { item: true, xs: 12 },
                    React.createElement(Typography, { sx: classes.description }, "Full years or specific dates in advanced mode")),
                timeSpanAvailable && temporalParametersEnabled ? (React.createElement(React.Fragment, null,
                    React.createElement(LocalizationProvider, { dateAdapter: AdapterDateFns },
                        React.createElement(DateWidget, { setTemporalParameter: setTemporalParameter, datasetsWithTimeSpan: datasetsWithTimeSpan })),
                    allDatasetsHaveTimespan ? React.createElement("div", null) : React.createElement(SomeTimespanAlert, null))) : temporalParametersEnabled ? (React.createElement(NoTimespanAlert, null)) : (React.createElement(ParameterUnsetAlert, null))))));
};
